import type { UserStory } from "@swivl-mono/types";
import { useState } from "react";

interface ObservedDependenciesPanelProps {
  observed: UserStory["observedDependencies"];
  /** Optional — when set, the empty-state nudges the tester to run the channel. */
  channel?: string;
}

/**
 * Read-only display of `UserStory.observedDependencies` — the variables, tools,
 * and prompt sections the bot actually touched the last time this story ran.
 *
 * Populated by the eval-runner's `evalCapture` AsyncLocalStorage hook + the
 * post-run StoryWriteback (dev-only). Critical for the audit: lets the tester
 * see what each story exercises so the property manager can be pruned.
 */
export function ObservedDependenciesPanel({ observed, channel }: ObservedDependenciesPanelProps): JSX.Element {
  const [expanded, setExpanded] = useState<{ vars: boolean; tools: boolean; prompts: boolean }>({ vars: false, tools: false, prompts: false });

  if (!observed) {
    return (
      <div className="rounded border border-dashed border-grey-200 bg-grey-50 px-3 py-2 text-xs text-grey-500">
        <strong className="block text-grey-600">No dependencies captured yet.</strong>
        Run this story{channel ? ` on ${channel}` : ""} to populate observed variables, tools, and prompt sections.
      </div>
    );
  }

  const { variableKeys, toolNames, promptSectionIds, capturedAt } = observed;

  return (
    <div className="rounded border border-grey-200 bg-grey-50 px-3 py-2 text-xs text-grey-700">
      <div className="mb-1 flex items-baseline justify-between">
        <strong className="text-grey-700">Dependencies (observed)</strong>
        <span className="text-[11px] text-grey-400">captured {capturedAt.slice(0, 16).replace("T", " ")}</span>
      </div>
      {!channel && (
        <p className="mb-2 text-[11px] text-grey-500">
          Aggregated across all channels the story has run on. Open the channel-specific run for a per-channel breakdown.
        </p>
      )}
      <DependencyList
        label="Variables read"
        items={variableKeys}
        expanded={expanded.vars}
        onToggle={() => setExpanded((p) => ({ ...p, vars: !p.vars }))}
      />
      <DependencyList
        label="Tools invoked"
        items={toolNames}
        expanded={expanded.tools}
        onToggle={() => setExpanded((p) => ({ ...p, tools: !p.tools }))}
      />
      <DependencyList
        label="Prompt sections"
        items={promptSectionIds}
        expanded={expanded.prompts}
        onToggle={() => setExpanded((p) => ({ ...p, prompts: !p.prompts }))}
      />
    </div>
  );
}

function DependencyList({
  label,
  items,
  expanded,
  onToggle
}: {
  label: string;
  items: string[];
  expanded: boolean;
  onToggle: () => void;
}): JSX.Element {
  const PREVIEW_COUNT = 6;
  const preview = items.slice(0, PREVIEW_COUNT);
  const overflow = items.length - PREVIEW_COUNT;

  return (
    <div className="border-t border-grey-100 py-1.5 first:border-t-0 first:pt-0">
      <div className="flex items-baseline justify-between">
        <span className="font-medium text-grey-700">
          {label} <span className="text-grey-400">({items.length})</span>
        </span>
        {items.length > PREVIEW_COUNT && (
          <button type="button" onClick={onToggle} className="text-[11px] text-orange-600 hover:underline">
            {expanded ? "Show less" : `Show all (${overflow} more)`}
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <span className="text-grey-400">— none —</span>
      ) : (
        <div className="mt-0.5 flex flex-wrap gap-1">
          {(expanded ? items : preview).map((item) => (
            <code key={item} className="rounded bg-white px-1.5 py-0.5 font-mono text-[11px] text-grey-700">
              {item}
            </code>
          ))}
          {!expanded && overflow > 0 && <span className="text-[11px] text-grey-400 italic">+{overflow} more</span>}
        </div>
      )}
    </div>
  );
}
